import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DESKTOP_SCREEN_SIZE } from 'app/common/personalConstants';
import { PaymentService } from 'app/shared/services/claims-payments.service';
import { FormErrorMapperService } from 'app/shared/services/form-error-mapper.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import {
    AccountState,
    BankAccountDetails,
    CloseActionStatus,
    EditState,
    LoadingState,
    ModalState,
    NotificationState,
    PaymentFormValue,
    PaymentMethod
} from './models/payment-preferences.model';
import {
    DEFAULT_ACCOUNT_SELECTION_RESULT,
    ExistingAccountSelectionStrategy,
    NewAccountSelectionStrategy,
    ReftAccountSelectionStrategy
} from './services/account-selection-strategy.service';
import { PaymentAccountFacadeService } from './services/payment-account-facade.service';
import { PaymentAccountUtilityService } from './services/payment-account-utility.service';
import { PaymentPreferencesFormService } from './services/payment-preferences-form.service';

@Component({
    selector: 'is-payment-preferences',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule],
    providers: [
        FormErrorMapperService,
        SessionService,
        LoggerService,
        PaymentService,
        PaymentPreferencesFormService,
        PaymentAccountFacadeService,
        PaymentAccountUtilityService,
        ReftAccountSelectionStrategy,
        NewAccountSelectionStrategy,
        ExistingAccountSelectionStrategy
    ],
    templateUrl: './payment-preferences.component.html',
    styleUrls: ['./payment-preferences.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PaymentPreferencesComponent implements OnInit {
    static readonly LOG_PREFIX = 'Payment Preferences Modal';
    private readonly READ_ONLY_ALLOWED_KEYS = new Set(['Tab', 'Shift', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

    @ViewChild('paymentPreferencesModal', { static: true }) paymentPreferencesModal: ElementRef;
    @ViewChild('successNotification') successNotification?: ElementRef<HTMLElement>;
    @Output() closeAction = new EventEmitter<{ status: CloseActionStatus; message: string }>();

    readonly checking = 'checking';
    readonly savings = 'savings';

    paymentPreferencesForm!: UntypedFormGroup;
    paymentMethods: PaymentMethod[] = [];
    retrievedAccountDetails: BankAccountDetails[] = [];

    // State objects
    loadingState: LoadingState = { isLoading: true, isSubmitting: false };
    notificationState: NotificationState = { success: false, failure: false, successMessage: '' };
    editState: EditState = { isEditing: false, hasAccountDetails: false, newAccount: false, isFormReadOnly: false, isSecondaryAccount: false };
    accountState: AccountState = { reftSelected: false, displayReftAccount: false, retrievedReftAccount: false };
    modalState: ModalState = { bankName: '', showBankName: false, accountPublicId: '' };

    // Session data
    claimNumber!: string;
    contactPublicId!: string;
    policyNumber!: string;
    userType!: string;
    billingAccountNumber!: string;

    private readonly NEW_ACCOUNT_KEYS = ['new-checking', 'new-savings'];
    private readonly REFT_ACCOUNT_KEY = 'reftAccount';

    // Compatibility getters for template
    get isLoading(): boolean {
        return this.loadingState.isLoading;
    }
    get accountUpdateSuccess(): boolean {
        return this.notificationState.success;
    }
    get accountUpdateFailure(): boolean {
        return this.notificationState.failure;
    }
    get successMsgAccountName(): string {
        return this.notificationState.successMessage;
    }
    get hasAccountDetails(): boolean {
        return this.editState.hasAccountDetails;
    }
    get isEditing(): boolean {
        return this.editState.isEditing;
    }
    get newAccount(): boolean {
        return this.editState.newAccount;
    }
    get isFormReadOnly(): boolean {
        return this.editState.isFormReadOnly;
    }
    get reftSelected(): boolean {
        return this.accountState.reftSelected;
    }
    get displayReftAccount(): boolean {
        return this.accountState.displayReftAccount;
    }
    get bankName(): string {
        return this.modalState.bankName;
    }
    get showBankName(): boolean {
        return this.modalState.showBankName;
    }

    get shouldShowCloseButton(): boolean {
        return ((this.accountUpdateSuccess || this.hasAccountDetails) &&
            !this.isEditing && !this.reftSelected && !this.newAccount) ||
            (this.reftSelected && this.accountUpdateSuccess && !this.isEditing) ||
            (!this.isLoading && !this.isEditing && !this.hasAccountDetails &&
                !this.reftSelected && !this.newAccount && !this.accountUpdateSuccess);
    }

    get shouldShowCancelButton(): boolean {
        return this.isLoading || ((this.isEditing || this.reftSelected || this.newAccount) && !this.accountUpdateSuccess);
    }

    get shouldShowEditButton(): boolean {
        return (this.hasAccountDetails && !this.isEditing && !this.reftSelected) || (this.reftSelected && this.accountUpdateSuccess && !this.isEditing);
    }

    get shouldShowSaveButton(): boolean {
        return this.isEditing || this.newAccount || (this.reftSelected && !this.accountUpdateSuccess);
    }

    constructor(
        public readonly formErrorService: FormErrorMapperService,
        private readonly sessionService: SessionService,
        private readonly loggerService: LoggerService,
        private readonly formService: PaymentPreferencesFormService,
        private readonly accountFacade: PaymentAccountFacadeService,
        private readonly utilityService: PaymentAccountUtilityService,
        private readonly reftStrategy: ReftAccountSelectionStrategy,
        private readonly newAccountStrategy: NewAccountSelectionStrategy,
        private readonly existingAccountStrategy: ExistingAccountSelectionStrategy,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.initializeSessionData();
        this.initializeForm();
        this.initializeLogger();
    }

    private initializeLogger(): void {
        this.loggerService.setComponentName('PaymentPreferencesComponent');
        this.loggerService.setPageName('Payment Preferences Modal');
        this.loggerService.info(`${PaymentPreferencesComponent.LOG_PREFIX} - Component Initialized`, {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
        });
    }

    private initializeSessionData(): void {
        this.claimNumber = this.sessionService.getClaimNumber();
        this.contactPublicId = this.sessionService.getSessionItem('claimsContactPublicId');
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    private initializeForm(): void {
        this.paymentPreferencesForm = this.formService.buildForm();
        this.formService.setFormReference(this.paymentPreferencesForm);
        this.formService.setupAccountNumberSync(this.paymentPreferencesForm);
        this.paymentMethods = this.utilityService.getDefaultPaymentMethods();
    }

    open(): void {
        this.loggerService.info(`${PaymentPreferencesComponent.LOG_PREFIX} - Modal Opened`, {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber
        });
        this.resetModalState();
        this.paymentPreferencesModal.nativeElement.openModal();
        Promise.all([this.loadAccountDetails(), this.loadReftInfo()])
            .finally(() => {
                this.loadingState.isLoading = false;
            });
    }

    close(): void {
        this.paymentPreferencesForm.reset();
        this.resetPaymentMethods();
        if (this.notificationState.success) {
            this.loggerService.info(`${PaymentPreferencesComponent.LOG_PREFIX} - Modal Closed`, { status: 'success' });
            this.closeAction.emit({ status: 'success', message: 'Account details updated successfully.' });
        } else if (this.notificationState.failure) {
            this.loggerService.info(`${PaymentPreferencesComponent.LOG_PREFIX} - Modal Closed`, { status: 'failure' });
            this.closeAction.emit({ status: 'failure', message: 'Account details update failed.' });
        } else {
            this.loggerService.info(`${PaymentPreferencesComponent.LOG_PREFIX} - Modal Closed`, { status: 'dismiss' });
            this.closeAction.emit({ status: 'dismiss', message: '' });
        }
        this.resetModalState();
        this.paymentPreferencesModal.nativeElement.closeModal();
    }

    private resetPaymentMethods(): void {
        this.paymentMethods = this.utilityService.getDefaultPaymentMethods();
        this.billingAccountNumber = '';
    }

    edit(): void {
        this.loggerService.info(`${PaymentPreferencesComponent.LOG_PREFIX} - Edit Account Details`);
        this.editState.isEditing = true;
        this.editState.isFormReadOnly = false;
        this.restoreFieldValidators();
        this.clearNotificationStates();
        if (this.accountState.reftSelected) {
            this.accountState.displayReftAccount = false;
        }
        if (this.editState.hasAccountDetails) {
            this.formService.clearSensitiveFields(this.paymentPreferencesForm);
        }
        setTimeout(() => {
            const firstField = document.getElementById('paymentMethod');
            firstField?.focus();
        }, 0);
    }

    private setNotificationState(success: boolean, failure: boolean, successMessage: string = ''): void {
        this.notificationState = { success, failure, successMessage };
    }

    private clearNotificationStates(): void {
        this.setNotificationState(false, false, '');
    }

    private focusSuccessNotification(): void {
        setTimeout(() => {
            this.successNotification?.nativeElement?.focus();
        }, 0);
    }

    submit(): void {
        this.loggerService.info(`${PaymentPreferencesComponent.LOG_PREFIX} - Submit Clicked`, {
            reftSelected: this.accountState.reftSelected,
            formValid: this.paymentPreferencesForm.valid,
            formValue: this.paymentPreferencesForm.value
        });

        if (this.accountState.reftSelected) {
            this.submitReftAccount();
        } else if (this.paymentPreferencesForm.valid) {
            this.submitBankAccount();
        }
    }


    onPaymentMethodChange(event: Event): void {
        this.clearNotificationStates();
        const target = event.target as HTMLSelectElement;
        const paymentMethodValue = target.value;

        const context = {
            form: this.paymentPreferencesForm,
            retrievedAccountDetails: this.retrievedAccountDetails,
            formService: this.formService
        };

        let result = DEFAULT_ACCOUNT_SELECTION_RESULT;
        if (paymentMethodValue) {
            if (paymentMethodValue === this.REFT_ACCOUNT_KEY) {
                result = this.reftStrategy.handle(context);
            } else if (this.NEW_ACCOUNT_KEYS.includes(paymentMethodValue)) {
                result = this.newAccountStrategy.handle(context, paymentMethodValue);
            } else {
                result = this.existingAccountStrategy.handle(context, paymentMethodValue);
            }
        }

        this.applySelectionResult(result);
    }

    onReadOnlyKeydown(event: KeyboardEvent): void {
        if (!this.isFormReadOnly) {
            return;
        }

        const isControlCombination = (event.ctrlKey || event.metaKey) && ['a', 'c'].includes(event.key.toLowerCase());

        if (this.READ_ONLY_ALLOWED_KEYS.has(event.key) || isControlCombination) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    }

    blockReadOnlyInput(event: Event): void {
        if (!this.isFormReadOnly) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    }

    private applySelectionResult(result: any): void {
        this.accountState.reftSelected = result.reftSelected;
        this.accountState.displayReftAccount = result.displayReftAccount;
        this.editState.isEditing = result.isEditing;
        this.editState.newAccount = result.newAccount;
        this.editState.hasAccountDetails = result.hasAccountDetails;
        this.editState.isFormReadOnly = result.isFormReadOnly ?? false;
        this.editState.isSecondaryAccount = result.isSecondaryAccount ?? false;
        this.modalState.bankName = result.bankName;
        this.modalState.showBankName = result.showBankName;

        if (this.isFormReadOnly) {
            this.clearFieldValidators();
        } else {
            this.restoreFieldValidators();
        }
    }

    async onBankRoutingNumberBlur(): Promise<void> {
        if (this.isFormReadOnly) {
            return;
        }

        const control = this.paymentPreferencesForm.get('bankRoutingNumber');

        if (!control?.valid) {
            this.clearBankNameDisplay();
            return;
        }

        try {
            this.modalState.bankName = await this.accountFacade.validateAndFetchBankDetails(control.value);
            this.modalState.showBankName = !!this.modalState.bankName;
        } catch (error) {
            this.clearBankNameDisplay();
            control.setErrors({ invalidRoutingNumber: true });
        }
    }

    private populateFormFromAccount(account: BankAccountDetails): void {
        this.formService.populateFormFromAccount(this.paymentPreferencesForm, account);
        this.modalState.bankName = account.bankName;
        this.editState.hasAccountDetails = true;
        this.modalState.showBankName = false;
        this.editState.isFormReadOnly = true;
        this.clearFieldValidators();
        this.editState.isEditing = false;
    }

    private clearBankNameDisplay(): void {
        this.modalState.bankName = '';
        this.modalState.showBankName = false;
    }

    private clearFieldValidators(): void {
        const fields = ['accountName', 'bankAccountType', 'bankRoutingNumber', 'bankAccountNumber'];
        fields.forEach((fieldName) => {
            const control = this.paymentPreferencesForm.get(fieldName);
            if (control) {
                control.clearValidators();
                control.updateValueAndValidity();
            }
        });
    }

    private restoreFieldValidators(): void {
        this.formService.setValidators(this.paymentPreferencesForm);
    }

    private resetModalState(): void {
        this.clearNotificationStates();
        this.editState = { isEditing: false, hasAccountDetails: false, newAccount: false, isFormReadOnly: false, isSecondaryAccount: false };
        this.loadingState = { isLoading: true, isSubmitting: false };
        this.accountState = { reftSelected: false, displayReftAccount: false, retrievedReftAccount: false };
        this.modalState = { bankName: '', showBankName: false, accountPublicId: '' };
    }

    async loadAccountDetails(): Promise<void> {
        const result = await this.accountFacade.loadBankAccountDetails(this.claimNumber, this.contactPublicId);

        const existingReftAccount = this.retrievedAccountDetails.find((acc) => acc.publicID === this.REFT_ACCOUNT_KEY);

        this.retrievedAccountDetails = existingReftAccount ? [existingReftAccount, ...result.accounts] : result.accounts;
        this.editState.hasAccountDetails = result.hasAccountDetails;

        if (result.accounts.length > 0) {
            const newMethods = this.utilityService.buildPaymentMethodOptions(result.accounts);
            this.paymentMethods = [...newMethods, ...this.paymentMethods];

            this.modalState.primaryAccount = result.primaryAccount;
            if (this.modalState.primaryAccount) {
                this.populateFormFromAccount(this.modalState.primaryAccount);
            }
        }
    }

    private async submitReftAccount(): Promise<void> {
        const claimDetails = {
            accountReferenceID: this.billingAccountNumber,
            claimNumber: this.claimNumber,
            contactID: this.contactPublicId
        };

        this.loadingState.isLoading = true;

        try {
            await this.accountFacade.saveReftAccount(claimDetails);
            this.editState.isEditing = false;
            this.accountState.displayReftAccount = true;

            const existingReftAccount = this.retrievedAccountDetails.find(
                (acc) => acc.publicID === this.REFT_ACCOUNT_KEY
            );

            if (existingReftAccount) {
                const successMessage = this.utilityService.buildSuccessMessage(
                    existingReftAccount.accountName,
                    existingReftAccount.bankAccountNumber
                );
                this.setNotificationState(true, false, successMessage);
                this.populateFormFromAccount(existingReftAccount);
                this.focusSuccessNotification();
            }
        } catch (error) {
            this.loggerService.error(`${PaymentPreferencesComponent.LOG_PREFIX} - Failed to save REFT account`, error);
            this.accountState.displayReftAccount = false;
            this.setNotificationState(false, true);
        } finally {
            this.loadingState.isLoading = false;
        }
    }

    private async submitBankAccount(): Promise<void> {
        const formValue = this.paymentPreferencesForm.value as PaymentFormValue;

        const details = {
            publicID: this.modalState.accountPublicId || 'new',
            bankRoutingNumber: formValue.bankRoutingNumber,
            accountName: formValue.accountName,
            bankAccountNumber: formValue.bankAccountNumber,
            bankName: this.modalState.bankName,
            bankAccountingType: formValue.bankAccountType,
            primaryAccount: true,
            contactID: this.contactPublicId,
            claimNumber: this.claimNumber
        };

        const successMessage = this.utilityService.buildSuccessMessage(
            details.accountName,
            details.bankAccountNumber
        );

        this.loadingState.isLoading = true;

        try {
            const accountList = await this.accountFacade.saveBankAccount(details);
            this.setNotificationState(true, false, successMessage);
            this.editState.isEditing = false;
            this.editState.newAccount = false;
            this.editState.isSecondaryAccount = false;

            const existingReftAccount = this.retrievedAccountDetails.find(
                (acc) => acc.publicID === this.REFT_ACCOUNT_KEY
            );

            this.resetPaymentMethods();
            const defaultMethods = [...this.paymentMethods]; // Save defaults [new-checking, new-savings]

            this.retrievedAccountDetails = existingReftAccount ?
                [existingReftAccount, ...accountList] :
                accountList;

            this.paymentMethods = [];

            if (existingReftAccount) {
                this.paymentMethods.push({
                    key: this.REFT_ACCOUNT_KEY,
                    value: this.utilityService.formatReftAccountLabel(existingReftAccount.bankAccountNumber)
                });
            }
            const savedAccountMethods = this.utilityService.buildPaymentMethodOptions(accountList);
            this.paymentMethods = [...this.paymentMethods, ...savedAccountMethods];

            this.paymentMethods = [...this.paymentMethods, ...defaultMethods];
            this.modalState.primaryAccount = accountList[0];
            if (this.modalState.primaryAccount) {
                this.populateFormFromAccount(this.modalState.primaryAccount);
            }
            this.focusSuccessNotification();
        } catch (error) {
            this.loggerService.error(`${PaymentPreferencesComponent.LOG_PREFIX} - Failed to save bank account`, error);
            this.setNotificationState(false, true);
        } finally {
            this.loadingState.isLoading = false;
        }
    }

    maskAccountNumber(raw?: string | number): string {
        return this.utilityService.maskAccountNumber(raw);
    }

    async loadReftInfo(): Promise<void> {
        const result = await this.accountFacade.loadReftAccount(this.policyNumber);

        if (result.hasReftAccount && result.accountDetails) {
            this.accountState.retrievedReftAccount = true;
            this.billingAccountNumber = result.billingAccountNumber || '';
            this.updateREFTinPaymentMethods(result.accountDetails);
        }
    }

    updateREFTinPaymentMethods(accountDetails: BankAccountDetails): void {
        this.paymentMethods.unshift({
            key: this.REFT_ACCOUNT_KEY,
            value: this.utilityService.formatReftAccountLabel(accountDetails.bankAccountNumber)
        });
        this.retrievedAccountDetails.unshift(accountDetails);
    }

    @HostListener('window:resize')
    onResize(): void {
        if (window.innerWidth >= DESKTOP_SCREEN_SIZE) {
            this.sessionService.setSessionItem('tab', 'Payments');
            this.router.navigate(['/property/tracker']);
        }
    }
}
